'use client';

import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import type { ReactNode } from 'react';
import { use, useEffect, useRef, useState } from 'react';

import { isWithinEmailVerificationGrace, ROUTES, SessionErrorKind } from '@repo/common';
import type {
  GetCurrentUserQuery,
  PictureInput,
  UpdateCurrentUserPictureMutation,
  UpdateProfileInput,
} from '@repo/frontend-shared';
import {
  DeleteCurrentUserPictureDocument,
  GetCurrentUserDocument,
  Role,
  UpdateCurrentUserDocument,
  UpdateCurrentUserPictureDocument,
} from '@repo/frontend-shared';
import { createLogger, isSafeAppPath } from '@repo/frontend-shared/utils';
import { classifyApolloSessionError } from '@repo/frontend-shared/utils/classify-apollo-session-error';
import { toast } from '@repo/sds/utils/toast';
import { PictureSavePersistenceError } from '@repo/ui/api/file-upload';
import { initializeApp } from '@repo/ui/utils/app-init';
import { betterAuthSignOut } from '@repo/ui/utils/open-auth-url';

import { resetStartHubPixelIdentity, syncStartHubPixelUser } from './analytics-context';
import { AUTH_TRANSITION_KIND, publishAuthTransition, useAuthTransitionListener } from './auth-transition';
import { subscribeToConsent } from './cookie-consent-engine';
import {
  type CompositeCurrentUserType,
  CurrentUserContext,
  type CurrentUserContextValue,
  type CurrentUserReloadResult,
} from './current-user-context-store';

type PictureUpdateResult = { succeeded: true } | { succeeded: false; error: PictureSavePersistenceError };
const sessionLogger = createLogger('current-user-session');
interface Props {
  children: ReactNode;
  logoutRoute?: string;
  /** API URI for app initialization (e.g., '/api'). If provided, waits for init before querying user. */
  apiUri?: string;
}

function useAppInitialization(apiUri: string | undefined): boolean {
  const [initializedApiUri, setInitializedApiUri] = useState<string | null>(null);
  const appInitialized = !apiUri || initializedApiUri === apiUri;

  useEffect(() => {
    if (!apiUri || appInitialized) return;

    let cancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const attemptInit = async (retryCount = 0): Promise<void> => {
      const result = await initializeApp(apiUri);
      if (cancelled) return;

      if (result.status === 'transient-failure') {
        const delayMs = Math.min(1_000 * 2 ** retryCount, 30_000);
        retryTimeoutId = setTimeout(() => {
          void attemptInit(retryCount + 1);
        }, delayMs);
        return;
      }

      // ok | unauthenticated | terminal-failure — stop retrying; guards handle auth UX.
      setInitializedApiUri(apiUri);
    };

    void attemptInit();

    return () => {
      cancelled = true;
      if (retryTimeoutId !== undefined) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [apiUri, appInitialized]);

  return appInitialized;
}

function useCurrentUserQuery(appInitialized: boolean) {
  const [getCurrentUser] = useLazyQuery(GetCurrentUserDocument, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const query = useQuery(GetCurrentUserDocument, {
    skip: !appInitialized,
    errorPolicy: 'all',
  });

  return { getCurrentUser, query };
}

function useSaveUserMetadata() {
  const apolloClient = useApolloClient();
  return (data: CompositeCurrentUserType) => {
    apolloClient.writeQuery({
      query: GetCurrentUserDocument,
      data: { __typename: 'Query', currentUser: data },
    });
  };
}

interface CurrentUserActionsOptions {
  currentUser: CompositeCurrentUserType;
  getCurrentUser: ReturnType<typeof useCurrentUserQuery>['getCurrentUser'];
  saveUserMetadata: (data: CompositeCurrentUserType) => void;
  logoutRoute: string;
}

function mergePictureMutationUser(
  currentUser: CompositeCurrentUserType,
  mutationUser: UpdateCurrentUserPictureMutation['updateCurrentUserPicture'],
): CompositeCurrentUserType {
  if (!currentUser || currentUser.id !== mutationUser.id) return currentUser;

  return {
    ...currentUser,
    fullName: mutationUser.fullName,
    firstName: mutationUser.firstName,
    lastName: mutationUser.lastName,
    email: mutationUser.email,
    picture: mutationUser.picture,
  };
}

type PictureMutationResult =
  | { succeeded: true; currentUser: UpdateCurrentUserPictureMutation['updateCurrentUserPicture'] }
  | { succeeded: false; error: unknown };

interface PictureMutationResponse {
  data?: UpdateCurrentUserPictureMutation | null;
  error?: unknown;
}

interface PictureUpdateResolutionOptions {
  picture: PictureInput;
  currentUser: CompositeCurrentUserType;
  runMutation: () => Promise<PictureMutationResponse>;
  fetchCurrentUser: () => Promise<CurrentUserReloadResult>;
  saveUserMetadata: (data: CompositeCurrentUserType) => void;
  isMetadataOperationCurrent: () => boolean;
  isPictureOperationCurrent: () => boolean;
}

async function runPictureMutation(runMutation: () => Promise<PictureMutationResponse>): Promise<PictureMutationResult> {
  return await Promise.resolve()
    .then(runMutation)
    .then(
      result => {
        const mutationUser = result.data?.updateCurrentUserPicture;
        return mutationUser
          ? ({ succeeded: true, currentUser: mutationUser } as const)
          : ({
              succeeded: false,
              error: result.error ?? new Error('Picture update returned no user'),
            } as const);
      },
      (error: unknown) => ({ succeeded: false, error }) as const,
    );
}

function pictureMatchesInput(currentUser: NonNullable<CompositeCurrentUserType>, picture: PictureInput): boolean {
  return (
    currentUser.picture?.originalId === picture.originalId && currentUser.picture?.thumbnailId === picture.thumbnailId
  );
}

function savePictureUserIfCurrent(
  currentUser: NonNullable<CompositeCurrentUserType>,
  options: PictureUpdateResolutionOptions,
): void {
  if (options.isMetadataOperationCurrent()) options.saveUserMetadata(currentUser);
}

function resolveRefreshedPicture(
  currentUser: NonNullable<CompositeCurrentUserType>,
  options: PictureUpdateResolutionOptions,
): PictureUpdateResult {
  savePictureUserIfCurrent(currentUser, options);
  if (pictureMatchesInput(currentUser, options.picture)) return { succeeded: true };

  return {
    succeeded: false,
    error: new PictureSavePersistenceError(
      'Picture was saved, but a newer profile picture is already active. Uploaded files were retained.',
      false,
    ),
  };
}

async function resolveSuccessfulPictureMutation(
  mutationUser: UpdateCurrentUserPictureMutation['updateCurrentUserPicture'],
  options: PictureUpdateResolutionOptions,
): Promise<PictureUpdateResult> {
  const refreshResult = await options.fetchCurrentUser();
  if (refreshResult.succeeded) return resolveRefreshedPicture(refreshResult.currentUser, options);

  const mutationState = mergePictureMutationUser(options.currentUser, mutationUser);
  if (mutationState) {
    savePictureUserIfCurrent(mutationState, options);
    return { succeeded: true };
  }

  return {
    succeeded: false,
    error: new PictureSavePersistenceError(
      'Picture was saved, but the current profile could not be updated. Uploaded files were retained.',
      false,
      refreshResult.error,
    ),
  };
}

async function resolveFailedPictureMutation(
  mutationError: unknown,
  options: PictureUpdateResolutionOptions,
): Promise<PictureUpdateResult> {
  const verificationResult = await options.fetchCurrentUser();
  if (!verificationResult.succeeded) {
    return {
      succeeded: false,
      error: new PictureSavePersistenceError(
        'Picture save could not be confirmed. Uploaded files were retained. Refresh your profile before retrying.',
        false,
        mutationError,
      ),
    };
  }

  savePictureUserIfCurrent(verificationResult.currentUser, options);
  if (pictureMatchesInput(verificationResult.currentUser, options.picture)) return { succeeded: true };

  return {
    succeeded: false,
    error: new PictureSavePersistenceError('Picture could not be saved. Please try again.', true, mutationError),
  };
}

async function resolveCompletedPictureUpdate(
  mutationResult: PictureMutationResult,
  options: PictureUpdateResolutionOptions,
): Promise<PictureUpdateResult> {
  if (!(options.isMetadataOperationCurrent() && options.isPictureOperationCurrent())) {
    return {
      succeeded: false,
      error: new PictureSavePersistenceError(
        'Picture save completed after a newer profile change. Uploaded files were retained. Refresh your profile before retrying.',
        false,
      ),
    };
  }

  if (mutationResult.succeeded) {
    return resolveSuccessfulPictureMutation(mutationResult.currentUser, options);
  }
  return resolveFailedPictureMutation(mutationResult.error, options);
}

async function resolvePictureUpdate(options: PictureUpdateResolutionOptions): Promise<PictureUpdateResult> {
  if (!(options.isMetadataOperationCurrent() && options.isPictureOperationCurrent())) {
    return {
      succeeded: false,
      error: new PictureSavePersistenceError(
        'A newer profile change superseded this picture save before it started.',
        true,
      ),
    };
  }

  const mutationResult = await runPictureMutation(options.runMutation);
  return resolveCompletedPictureUpdate(mutationResult, options);
}

/** After sign-out the page goes to a same-origin `returnTo` when one is given, never to another site. */
export function resolveLogoutDestination(returnTo: string | undefined, logoutRoute: string): string {
  return returnTo && isSafeAppPath(returnTo) ? returnTo : logoutRoute;
}

function useCurrentUserActions({
  currentUser,
  getCurrentUser,
  saveUserMetadata,
  logoutRoute,
}: CurrentUserActionsOptions) {
  const apolloClient = useApolloClient();
  const [updateCurrentUserMutation] = useMutation(UpdateCurrentUserDocument, { fetchPolicy: 'no-cache' });
  const [updateCurrentUserPictureMutation] = useMutation(UpdateCurrentUserPictureDocument, { fetchPolicy: 'no-cache' });
  const [deleteCurrentUserPictureMutation] = useMutation(DeleteCurrentUserPictureDocument, { fetchPolicy: 'no-cache' });

  const [saving, setSaving] = useState<boolean>(false);
  const [savingPicture, setSavingPicture] = useState<boolean>(false);
  const metadataOperationId = useRef(0);
  const pictureOperationId = useRef(0);

  const fetchCurrentUser = async (): Promise<CurrentUserReloadResult> => {
    try {
      // An explicit reload must observe state written by the action that
      // requested it. Apollo otherwise deduplicates this call against an
      // older in-flight GetCurrentUser request, which can restore stale
      // auth metadata after transitions such as email verification.
      const result = await getCurrentUser({ context: { queryDeduplication: false } });
      const freshUser = result.data?.currentUser;
      if (freshUser) return { succeeded: true, currentUser: freshUser };
      if (result.error) return { succeeded: false, error: result.error };

      return { succeeded: false, error: new Error('Current user refresh returned no user') };
    } catch (error) {
      return { succeeded: false, error };
    }
  };

  const reloadCurrentUser = async (): Promise<CurrentUserReloadResult> => {
    const operationId = ++metadataOperationId.current;
    const result = await fetchCurrentUser();
    if (result.succeeded && metadataOperationId.current === operationId) saveUserMetadata(result.currentUser);
    return result;
  };

  const updateUserData = async (data: UpdateProfileInput) => {
    const metadataId = ++metadataOperationId.current;
    setSaving(true);
    const updateSucceeded = await updateCurrentUserMutation({
      variables: {
        input: data,
      },
    })
      .then(result => {
        if (result.data?.updateCurrentUser && metadataOperationId.current === metadataId) {
          saveUserMetadata(result.data.updateCurrentUser);
        }
        return true;
      })
      .then(
        result => result,
        () => false,
      );

    if (!updateSucceeded) toast.error('Error updating data.');
    setSaving(false);
  };

  const updatePicture = async (picture: PictureInput) => {
    const metadataId = ++metadataOperationId.current;
    const pictureId = ++pictureOperationId.current;
    setSavingPicture(true);
    const pictureUpdateResult = await resolvePictureUpdate({
      picture,
      currentUser,
      runMutation: () => updateCurrentUserPictureMutation({ variables: { input: picture } }),
      fetchCurrentUser,
      saveUserMetadata,
      isMetadataOperationCurrent: () => metadataOperationId.current === metadataId,
      isPictureOperationCurrent: () => pictureOperationId.current === pictureId,
    }).finally(() => {
      if (pictureOperationId.current === pictureId) setSavingPicture(false);
    });

    if (!pictureUpdateResult.succeeded) {
      throw pictureUpdateResult.error;
    }
  };

  const deletePicture = async () => {
    const metadataId = ++metadataOperationId.current;
    const pictureId = ++pictureOperationId.current;
    setSavingPicture(true);
    const deletionSucceeded = await Promise.resolve()
      .then(async () => {
        const mutationResult = await Promise.resolve()
          .then(() => deleteCurrentUserPictureMutation())
          .then(
            result => {
              const mutationUser = result.data?.deleteCurrentUserPicture;
              return mutationUser
                ? ({ succeeded: true, currentUser: mutationUser } as const)
                : ({
                    succeeded: false,
                    error: result.error ?? new Error('Picture deletion returned no user'),
                  } as const);
            },
            (error: unknown) => ({ succeeded: false, error }) as const,
          );

        const refreshResult = await Promise.resolve(mutationResult).then(() => fetchCurrentUser());
        if (refreshResult.succeeded) {
          if (metadataOperationId.current === metadataId) saveUserMetadata(refreshResult.currentUser);
          return refreshResult.currentUser.picture === null;
        }

        if (mutationResult.succeeded) {
          const mutationState =
            currentUser &&
            currentUser.id === mutationResult.currentUser.id &&
            mutationResult.currentUser.picture === null
              ? { ...currentUser, picture: null }
              : null;
          if (mutationState) {
            if (metadataOperationId.current === metadataId) saveUserMetadata(mutationState);
            return true;
          }
        }

        return false;
      })
      .then(
        result => result,
        () => false,
      );

    if (pictureOperationId.current === pictureId) {
      if (!deletionSucceeded) toast.error('Error deleting picture.');
      setSavingPicture(false);
    }
  };

  const finishLogout = async (returnTo?: string) => {
    resetStartHubPixelIdentity();
    await apolloClient.clearStore().catch(error => {
      sessionLogger.error('Failed to clear local cache after session revocation', error);
    });
    publishAuthTransition(AUTH_TRANSITION_KIND.SignedOut);
    window.location.href = resolveLogoutDestination(returnTo, logoutRoute);
  };

  const signOutThen = async (returnTo?: string): Promise<boolean> => {
    try {
      await betterAuthSignOut();
    } catch {
      toast.error('Error logging out. Please try again.');
      return false;
    }
    await finishLogout(returnTo);
    return true;
  };
  const completeLogout = () => finishLogout();
  const logout = () => signOutThen();
  const logoutAndReturnTo = (returnTo: string) => signOutThen(returnTo);

  return {
    saving,
    savingPicture,
    updateUserData,
    updatePicture,
    deletePicture,
    reloadCurrentUser,
    logout,
    logoutAndReturnTo,
    completeLogout,
  };
}

function buildCurrentUserContextValue(
  currentUser: CompositeCurrentUserType,
  loading: boolean,
  actions: ReturnType<typeof useCurrentUserActions>,
): CurrentUserContextValue {
  const hasRole = (role: Role): boolean => currentUser?.role === role;
  return {
    currentUser,
    loading,
    saving: actions.saving,
    savingPicture: actions.savingPicture,
    shouldVerifyEmail: currentUser
      ? !(currentUser.isEmailVerified || isWithinEmailVerificationGrace(currentUser.createdAt))
      : false,
    hasUnconfirmedEmail: currentUser ? !currentUser.isEmailVerified : false,
    isLoggedIn: Boolean(currentUser),
    isMentor: hasRole(Role.MENTOR),
    isAdmin: hasRole(Role.ADMIN),
    isSuperAdmin: hasRole(Role.SUPER_ADMIN),
    isStudent: hasRole(Role.STUDENT),
    isPartnerManager: hasRole(Role.PARTNER_MANAGER),
    updateUserData: actions.updateUserData,
    updatePicture: actions.updatePicture,
    deletePicture: actions.deletePicture,
    reloadCurrentUser: actions.reloadCurrentUser,
    logout: actions.logout,
    logoutAndReturnTo: actions.logoutAndReturnTo,
    completeLogout: actions.completeLogout,
  };
}

interface CurrentUserQueryState {
  data: GetCurrentUserQuery | undefined;
  error?: unknown;
  loading: boolean;
}

function isCurrentUserLoading(appInitialized: boolean, query: CurrentUserQueryState): boolean {
  if (!appInitialized || query.loading) return true;
  if (query.data?.currentUser) return false;
  // Transient (5xx/network) stays pending so guards do not treat it as logout.
  return classifyApolloSessionError(query.error) === SessionErrorKind.Transient;
}

export function CurrentUserContextProvider({ children, apiUri, logoutRoute = ROUTES.HOME }: Props) {
  const appInitialized = useAppInitialization(apiUri);
  useAuthTransitionListener(appInitialized);
  const { getCurrentUser, query } = useCurrentUserQuery(appInitialized);
  const saveUserMetadata = useSaveUserMetadata();
  const currentUser = query.data?.currentUser ?? null;
  const actions = useCurrentUserActions({ currentUser, getCurrentUser, saveUserMetadata, logoutRoute });

  const value = buildCurrentUserContextValue(currentUser, isCurrentUserLoading(appInitialized, query), actions);

  useEffect(() => {
    if (value.loading) return;
    const sync = () => {
      void syncStartHubPixelUser(currentUser);
    };
    sync();
    return subscribeToConsent(sync);
  }, [currentUser, value.loading]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export const useCurrentUser = () => use(CurrentUserContext);
